import { useEffect, useState } from "react";
import { FaEdit, FaSort, FaSortDown, FaSortUp, FaTrash } from "react-icons/fa";
import { fetchWithAuth } from "../api/fetchWithAuth";
import EraTestCaseFormModal from "../components/EraTestCaseFormModal";
import EraTestCaseModal from "../components/EraTestCaseModal";

export default function EraTestCases() {
  const [testCases, setTestCases] = useState([]);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showDmiIn, setShowDmiIn] = useState(false);
  const [showDmiOut, setShowDmiOut] = useState(false);
  const [sortField, setSortField] = useState("ft_number");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    loadTestCases();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  const loadTestCases = async () => {
    try {
      const res = await fetchWithAuth("/api/era-test-cases");

      if (!res.ok) {
        console.error("Failed to load ERA test cases");
        return;
      }

      const data = await res.json();
      setTestCases(data);
    } catch (err) {
      console.warn("loadTestCases aborted:", err.message);
    }
  };

  const deleteTestCase = async (id) => {
    if (!window.confirm("Delete this ERA test case?")) return;

    const res = await fetchWithAuth(`/api/era-test-cases/${id}`, {
      method: "DELETE"
    });

    if (!res.ok) {
      alert("Delete failed");
      return;
    }

    loadTestCases();
  };

  const openTestCase = (testCase) => {
    setSelectedTestCase(testCase);
    setShowModal(true);
  };

  const filteredTestCases = testCases.filter((testCase) => {
    const query = debouncedSearch.trim().toLowerCase();
    const matchesSearch = !query || (
      String(testCase.ft_number ?? "").toLowerCase().includes(query) ||
      String(testCase.tc_number ?? "").toLowerCase().includes(query) ||
      String(testCase.tc_title ?? "").toLowerCase().includes(query)
    );

    if (!matchesSearch) return false;

    const hasDmiIn = Number(testCase.dmi_in_count ?? 0) > 0;
    const hasDmiOut = Number(testCase.dmi_out_count ?? 0) > 0;

    if (showDmiIn && showDmiOut) {
      return hasDmiIn || hasDmiOut;
    }

    if (showDmiIn) {
      return hasDmiIn;
    }

    if (showDmiOut) {
      return hasDmiOut;
    }

    return true;
  });

  const compareFeatureNumbers = (leftValue, rightValue) => {
    const leftParts = String(leftValue ?? "").split(".");
    const rightParts = String(rightValue ?? "").split(".");
    const maxLength = Math.max(leftParts.length, rightParts.length);

    for (let index = 0; index < maxLength; index += 1) {
      const leftPart = leftParts[index];
      const rightPart = rightParts[index];

      if (leftPart === undefined) return -1;
      if (rightPart === undefined) return 1;

      const leftNumber = Number(leftPart);
      const rightNumber = Number(rightPart);
      const bothNumeric = !Number.isNaN(leftNumber) && !Number.isNaN(rightNumber);

      if (bothNumeric) {
        if (leftNumber < rightNumber) return -1;
        if (leftNumber > rightNumber) return 1;
        continue;
      }

      const textCompare = leftPart.localeCompare(rightPart, undefined, { sensitivity: "base" });
      if (textCompare !== 0) return textCompare;
    }

    return 0;
  };

  const compareTestCaseNumbers = (leftValue, rightValue) => {
    const leftNumber = Number(leftValue);
    const rightNumber = Number(rightValue);

    if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
      return leftNumber - rightNumber;
    }

    return String(leftValue ?? "").localeCompare(String(rightValue ?? ""), undefined, {
      sensitivity: "base",
      numeric: true
    });
  };

  const compareTestCases = (leftTestCase, rightTestCase) => {
    const direction = sortDir === "asc" ? 1 : -1;

    if (sortField === "ft_number") {
      const featureCompare = compareFeatureNumbers(leftTestCase.ft_number, rightTestCase.ft_number);
      if (featureCompare !== 0) return featureCompare * direction;
    }

    if (sortField === "tc_number") {
      const tcCompare = compareTestCaseNumbers(leftTestCase.tc_number, rightTestCase.tc_number);
      if (tcCompare !== 0) return tcCompare * direction;
    }

    if (sortField === "dmi_in_count") {
      const inCompare = Number(leftTestCase.dmi_in_count ?? 0) - Number(rightTestCase.dmi_in_count ?? 0);
      if (inCompare !== 0) return inCompare * direction;
    }

    if (sortField === "dmi_out_count") {
      const outCompare = Number(leftTestCase.dmi_out_count ?? 0) - Number(rightTestCase.dmi_out_count ?? 0);
      if (outCompare !== 0) return outCompare * direction;
    }

    if (sortField === "tc_title") {
      const titleCompare = String(leftTestCase.tc_title ?? "").localeCompare(
        String(rightTestCase.tc_title ?? ""),
        undefined,
        { sensitivity: "base" }
      );

      if (titleCompare !== 0) return titleCompare * direction;
    }

    const featureCompare = compareFeatureNumbers(leftTestCase.ft_number, rightTestCase.ft_number);
    if (featureCompare !== 0) return featureCompare;

    const tcCompare = compareTestCaseNumbers(leftTestCase.tc_number, rightTestCase.tc_number);
    if (tcCompare !== 0) return tcCompare;

    return String(leftTestCase.tc_title ?? "").localeCompare(
      String(rightTestCase.tc_title ?? ""),
      undefined,
      { sensitivity: "base" }
    );
  };

  const sortedTestCases = [...filteredTestCases].sort(compareTestCases);

  const renderHighlightedText = (value) => {
    const text = String(value ?? "");
    const query = debouncedSearch.trim();

    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let startIndex = 0;
    let matchIndex = lowerText.indexOf(lowerQuery);

    while (matchIndex !== -1) {
      if (matchIndex > startIndex) {
        parts.push(text.slice(startIndex, matchIndex));
      }

      const endIndex = matchIndex + query.length;
      parts.push(
        <mark key={`${text}-${matchIndex}`} className="px-0 bg-warning-subtle">
          {text.slice(matchIndex, endIndex)}
        </mark>
      );

      startIndex = endIndex;
      matchIndex = lowerText.indexOf(lowerQuery, startIndex);
    }

    if (startIndex < text.length) {
      parts.push(text.slice(startIndex));
    }

    return parts.length ? parts : text;
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir((current) => current === "asc" ? "desc" : "asc");
      return;
    }

    setSortField(field);
    setSortDir("asc");
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <FaSort className="ms-1 text-muted" aria-hidden="true" />;
    }

    if (sortDir === "asc") {
      return <FaSortUp className="ms-1" aria-hidden="true" />;
    }

    return <FaSortDown className="ms-1" aria-hidden="true" />;
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
        <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
          <input
            type="text"
            className="form-control pe-5"
            placeholder="Search ERA test cases..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
              }}
              aria-label="Clear search"
            >
              x
            </button>
          )}
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className={`btn btn-sm ${showDmiIn ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setShowDmiIn((current) => !current)}
          >
            DMI IN
          </button>
          <button
            type="button"
            className={`btn btn-sm ${showDmiOut ? "btn-success" : "btn-outline-success"}`}
            onClick={() => setShowDmiOut((current) => !current)}
          >
            DMI OUT
          </button>
          <span className="badge text-bg-secondary">
            Total: {filteredTestCases.length}
          </span>
        </div>
      </div>

      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th
                style={{ ...stickyHeaderStyle, width: "140px", cursor: "pointer" }}
                onClick={() => handleSort("ft_number")}
              >
                <span className="d-inline-flex align-items-center">
                  Feature
                  {renderSortIndicator("ft_number")}
                </span>
              </th>
              <th
                style={{ ...stickyHeaderStyle, width: "110px", cursor: "pointer" }}
                onClick={() => handleSort("tc_number")}
              >
                <span className="d-inline-flex align-items-center">
                  TC number
                  {renderSortIndicator("tc_number")}
                </span>
              </th>
              <th
                className="text-center"
                style={{ ...stickyHeaderStyle, width: "90px", cursor: "pointer" }}
                onClick={() => handleSort("dmi_in_count")}
              >
                <span className="d-inline-flex align-items-center">
                  DMI IN
                  {renderSortIndicator("dmi_in_count")}
                </span>
              </th>
              <th
                className="text-center"
                style={{ ...stickyHeaderStyle, width: "90px", cursor: "pointer" }}
                onClick={() => handleSort("dmi_out_count")}
              >
                <span className="d-inline-flex align-items-center">
                  DMI OUT
                  {renderSortIndicator("dmi_out_count")}
                </span>
              </th>
              <th
                style={{ ...stickyHeaderStyle, cursor: "pointer" }}
                onClick={() => handleSort("tc_title")}
              >
                <span className="d-inline-flex align-items-center">
                  Title
                  {renderSortIndicator("tc_title")}
                </span>
              </th>
              <th style={{ ...stickyHeaderStyle, width: "120px" }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {sortedTestCases.map((testCase, index) => (
              <tr key={testCase.id} style={getRowStyle(index)}>
                <td className="fw-bold">{renderHighlightedText(testCase.ft_number)}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => openTestCase(testCase)}
                  >
                    {renderHighlightedText(testCase.tc_number)}
                  </button>
                </td>
                <td className="text-center">
                  {Number(testCase.dmi_in_count ?? 0) > 0 && (
                    <span className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle">
                      {testCase.dmi_in_count}
                    </span>
                  )}
                </td>
                <td className="text-center">
                  {Number(testCase.dmi_out_count ?? 0) > 0 && (
                    <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle">
                      {testCase.dmi_out_count}
                    </span>
                  )}
                </td>
                <td>{renderHighlightedText(testCase.tc_title)}</td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => {
                      setEditingTestCase(testCase);
                      setShowForm(true);
                    }}
                  >
                    <FaEdit />
                  </button>

                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => deleteTestCase(testCase.id)}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EraTestCaseFormModal
        show={showForm}
        onClose={() => setShowForm(false)}
        onSaved={loadTestCases}
        testCase={editingTestCase}
      />

      <EraTestCaseModal
        show={showModal}
        onClose={() => setShowModal(false)}
        testCase={selectedTestCase}
      />
    </div>
  );
}
